// PortalLexa: Lexa, the AI career and interview coach, on the CRM layout.
//
// The main column says what she does and starts a session (a mock interview
// for a real role, or a career coaching conversation), then lists every past
// session with its recording, transcript, report and PDF. The side rail holds
// the sessions they have, buying another through Stripe's own page, what Lexa
// remembers, and the plain facts about privacy.
//
// Lexa is sold on what she does for the candidate, tied to the role in front
// of them, never on price. Prices are shown once, quietly, because the terms
// require them. Optional and separate: nothing here touches applications.
// P2-PORTAL (22 Oct 2026), from C1-LEXA3.
import { useState, useEffect, useCallback, useMemo } from 'react';
import API_BASE from '../../../config';
import { filterRoles } from '../../../components/gtSearch'; // C1-SEARCH
import GtLexaSession, { TOPIC_LABEL, ukDate, mins, statusOf, downloadPdf } from '../../../components/GtLexaSession';
import { Ico, ICONS, readLexaIntent, clearLexaIntent } from './portalKit';

function useApi(getToken) {
  return useCallback(async (p, init) => {
    const t = await getToken();
    const r = await fetch(API_BASE + p, { ...(init || {}), headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + t } });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) { const e = new Error(j.message || 'Something went wrong. Please try again.'); e.code = j.error; e.extra = j; throw e; }
    return j;
  }, [getToken]);
}

const TOPIC_HINT = {
  cv: 'How your CV tells your story, and how to position yourself',
  next_move: 'Where to go next, and how to get there',
  salary: 'Pay, offers, and how to negotiate them',
  linkedin: 'Your profile, headline and personal brand',
  confidence: 'Nerves, presence and feeling ready',
  other: 'Bring whatever is on your mind',
};

function Terms({ terms, onAgreed, api }) {
  const [tick, setTick] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  async function agree() {
    setBusy(true); setError('');
    try { await api('/api/portal/lexa/terms', { method: 'POST', body: JSON.stringify({ agree: true, version: terms.version }) }); onAgreed(); } catch (e) { setError(e.message); } finally { setBusy(false); }
  }
  return (
    <div className="gtr-card gtr2-tabcard">
      <div className="gtp2-cardhead"><div><b>{terms.title}</b><span>Read these once before your first session. We ask again only if they change.</span></div></div>
      <div className="gtr2-pad">
        <p className="gtp2-terms-intro">{terms.intro}</p>
        <ol className="gtp2-terms">
          {terms.clauses.map((c, i) => <li key={i}><b>{c.heading}</b> {c.text}</li>)}
        </ol>
        <label className="gtp2-tick">
          <input type="checkbox" checked={tick} onChange={(e) => setTick(e.target.checked)} />
          <span>I have read and agree to the Lexa terms.</span>
        </label>
        {error && <p className="gtp2-alert gtp2-alert--err">{error}</p>}
        <div className="gtp2-go">
          {!tick && <span className="gtp2-need">Tick the box to agree first</span>}
          <button type="button" className="gtr2-btn gtr2-btn--primary" disabled={!tick || busy} onClick={agree}>{busy ? 'One moment' : 'Agree and continue'}</button>
        </div>
      </div>
    </div>
  );
}

function Setup({ kind, api, initialRole, onCancel, onNoCredit }) {
  const [roles, setRoles] = useState([]);
  const [q, setQ] = useState('');
  const [roleRef, setRoleRef] = useState(initialRole || '');
  const [paste, setPaste] = useState(false);
  const [jd, setJd] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [mode, setMode] = useState('practice');
  const [topic, setTopic] = useState('');
  const [goal, setGoal] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (kind !== 'interview') return;
    fetch(API_BASE + '/api/public/roles').then((r) => r.json()).then((j) => setRoles(Array.isArray(j) ? j : (j.roles || []))).catch(() => setRoles([]));
  }, [kind]);
  const shown = useMemo(() => filterRoles(roles, q).slice(0, 8), [roles, q]); // C1-SEARCH
  const picked = roles.find((r) => r.ref === roleRef);
  const ready = kind === 'interview' ? (paste ? jd.trim().length >= 80 : !!roleRef) : !!topic && !!goal.trim();
  const missing = kind === 'interview'
    ? (paste ? (jd.trim().length < 80 ? 'Paste the full job description' : '') : (!roleRef ? 'Choose a role' : ''))
    : (!topic ? 'Choose a topic' : !goal.trim() ? 'Say what you want out of it' : '');

  async function start() {
    setBusy(true); setError('');
    const body = kind === 'interview'
      ? { kind, mode, goal: goal.trim() || undefined, ...(paste ? { job_description: jd, role_title: roleTitle.trim() || undefined } : { role_ref: roleRef }) }
      : { kind, topic, goal: goal.trim() };
    try {
      const r = await api('/api/portal/lexa/sessions', { method: 'POST', body: JSON.stringify(body) });
      clearLexaIntent();
      window.location.assign('/lexa/' + r.token);
    } catch (e) {
      if (e.code === 'no_credit') onNoCredit();
      setError(e.code === 'open_session' ? 'You already have a session waiting. Carry on with it or discard it first.' : e.message);
      setBusy(false);
    }
  }

  return (
    <div className="gtr-card gtr2-tabcard">
      <div className="gtp2-cardhead">
        <div>
          <b>{kind === 'interview' ? 'Which interview are you preparing for?' : 'What would you like to talk about?'}</b>
          <span>{kind === 'interview' ? 'Lexa builds the questions from the role and your CV.' : 'Lexa opens from what you tell her and closes with a plan you agree together.'}</span>
        </div>
        <button type="button" className="gtr2-back" onClick={onCancel}><Ico d={ICONS.back} />Back</button>
      </div>
      <div className="gtr2-pad gtp2-form">
        {kind === 'interview' ? (
          <>
            {!paste ? (
              <div className="gtp2-field">
                <span className="gtp2-k">A role with Generative Talent Co</span>
                {picked ? (
                  <div className="gtp2-picked"><span className="gtr2-listico"><Ico d={ICONS.brief} /></span><b>{picked.title}</b><button type="button" className="gtp2-linkbtn" onClick={() => setRoleRef('')}>Change</button></div>
                ) : (
                  <>
                    <label className="gtp2-search gtp2-search--in"><Ico d={ICONS.search} /><input placeholder="Search open roles" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search open roles" /></label>
                    <div className="gtp2-rolepick">
                      {shown.map((r) => <button key={r.ref} type="button" className="gtp2-pill" onClick={() => setRoleRef(r.ref)}>{r.title}</button>)}
                      {roles.length === 0 && <span className="gtr2-muted">No open roles right now. Paste a job description instead.</span>}
                    </div>
                  </>
                )}
                <button type="button" className="gtp2-linkbtn" onClick={() => setPaste(true)}>Preparing for a job somewhere else? Paste its description</button>
              </div>
            ) : (
              <div className="gtp2-field">
                <span className="gtp2-k">The job you are preparing for</span>
                <input className="gtp2-in" placeholder="Job title (optional)" value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)} aria-label="Job title" />
                <textarea className="gtp2-in" rows={6} placeholder="Paste the job description" value={jd} onChange={(e) => setJd(e.target.value)} aria-label="Job description" />
                <button type="button" className="gtp2-linkbtn" onClick={() => setPaste(false)}>Choose one of our open roles instead</button>
              </div>
            )}
            <div className="gtp2-field">
              <span className="gtp2-k">How would you like to practise?</span>
              <div className="gtp2-choices">
                <button type="button" className={'gtp2-choice' + (mode === 'practice' ? ' gtp2-choice--on' : '')} onClick={() => setMode('practice')} aria-pressed={mode === 'practice'}>
                  <b>Practice mode</b><span>Lexa coaches you after each answer and you can try again before moving on.</span>
                </button>
                <button type="button" className={'gtp2-choice' + (mode === 'real' ? ' gtp2-choice--on' : '')} onClick={() => setMode('real')} aria-pressed={mode === 'real'}>
                  <b>Real mode</b><span>A realistic interview straight through, then a spoken debrief at the end.</span>
                </button>
              </div>
            </div>
            <div className="gtp2-field">
              <span className="gtp2-k">Anything you want to work on? <em>Optional</em></span>
              <input className="gtp2-in" placeholder="For example: I ramble on the technical questions" value={goal} onChange={(e) => setGoal(e.target.value)} aria-label="What to work on" />
            </div>
          </>
        ) : (
          <>
            <div className="gtp2-choices gtp2-choices--3">
              {Object.keys(TOPIC_LABEL).map((k) => (
                <button key={k} type="button" className={'gtp2-choice' + (topic === k ? ' gtp2-choice--on' : '')} onClick={() => setTopic(k)} aria-pressed={topic === k}>
                  <b>{TOPIC_LABEL[k]}</b><span>{TOPIC_HINT[k]}</span>
                </button>
              ))}
            </div>
            <div className="gtp2-field">
              <span className="gtp2-k">What do you want to get out of it?</span>
              <textarea className="gtp2-in" rows={3} placeholder="A line or two is plenty" value={goal} onChange={(e) => setGoal(e.target.value)} aria-label="What you want from the session" />
            </div>
          </>
        )}
        {error && <p className="gtp2-alert gtp2-alert--err">{error}</p>}
        <div className="gtp2-go gtp2-go--split">
          <span className="gtr2-muted">About 20 minutes on video. A session is only used when the call starts.</span>
          <span className="gtp2-go">
            {!ready && missing && <span className="gtp2-need">{missing}</span>}
            <button type="button" className="gtr2-btn gtr2-btn--primary" disabled={!ready || busy} onClick={start}>{busy ? 'Setting up' : 'Set up and start'}</button>
          </span>
        </div>
      </div>
    </div>
  );
}

function SessionModal({ id, api, getToken, onClose }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState('');
  const [pdfBusy, setPdfBusy] = useState(false);
  const load = useCallback(async () => {
    try { setD(await api('/api/portal/lexa/sessions/' + id)); } catch (e) { setError(e.message); }
  }, [api, id]);
  useEffect(() => { load(); }, [load]);
  const waiting = d && d.status === 'completed' && !d.report && d.report_status !== 'failed';
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [waiting, load]);
  async function pdf() {
    setPdfBusy(true);
    try { await downloadPdf(getToken, '/api/portal/lexa/sessions/' + id + '/pdf'); } catch (e) { setError(e.message); } finally { setPdfBusy(false); }
  }
  return (
    <div className="gtc-modal-backdrop" onClick={onClose}>
      <div className="gtc-modal gtlx-modal" onClick={(e) => e.stopPropagation()}>
        {!d ? (
          <div className="gtc-modal-body">{error ? <p className="gtp2-alert gtp2-alert--err">{error}</p> : <p className="gtr2-muted">Loading</p>}</div>
        ) : (
          <>
            <div className="gtlx-modal-h">
              <span className="gtlx-orb" aria-hidden="true" />
              <div className="gtlx-modal-t"><b>{d.title}</b><span>{ukDate(d.started_at || d.created_at)}{d.duration_seconds ? ' · ' + mins(d.duration_seconds) : ''}</span></div>
              {d.report && !d.report.short && <button type="button" className="gtc-btn-ghost gtlx-pdfbtn" disabled={pdfBusy} onClick={pdf}>{pdfBusy ? 'Preparing' : 'Download PDF'}</button>}
              <button type="button" className="gtivl-x" onClick={onClose} aria-label="Close">×</button>
            </div>
            <GtLexaSession d={d} admin={false} />
          </>
        )}
      </div>
    </div>
  );
}

export default function PortalLexa({ getToken, onNeedCv, onChanged }) {
  const api = useApi(getToken);
  const [intent] = useState(readLexaIntent);
  const [state, setState] = useState(null);
  const [list, setList] = useState(null);
  const [error, setError] = useState('');
  const [kind, setKind] = useState(intent ? intent.kind : null);
  const [currency, setCurrency] = useState('gbp');
  const [buying, setBuying] = useState(false);
  const [toast, setToast] = useState(null);
  const [viewId, setViewId] = useState(null);
  const [nudgeBuy, setNudgeBuy] = useState(false);
  const flash = useCallback((text, bad) => { setToast({ text, bad }); setTimeout(() => setToast(null), bad ? 7000 : 4000); }, []);

  const load = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([api('/api/portal/lexa'), api('/api/portal/lexa/sessions')]);
      setState(s); setList(l); setError('');
    } catch (e) { setError(e.code === 'no_profile' ? 'no_profile' : e.message); }
  }, [api]);
  useEffect(() => { load(); }, [load]);

  // Back from Stripe: confirm the payment straight away. ?lexa=prep is the
  // job-page link; the intent it carries is already in sessionStorage.
  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const flag = qs.get('lexa');
    if (!flag) return;
    try { window.history.replaceState(null, '', window.location.pathname); } catch (e) { /* fine */ }
    if (flag === 'paid' && qs.get('cs')) {
      api('/api/portal/lexa/checkout/' + encodeURIComponent(qs.get('cs')))
        .then((r) => { flash(r.status === 'paid' ? 'Payment received. Your session is ready to use.' : 'Your payment is still going through. It will show here in a moment.'); load(); if (onChanged) onChanged(); })
        .catch((e) => flash(e.message, true));
    } else if (flag === 'cancelled') flash('Payment cancelled. Nothing was charged.');
  }, [api, flash, load, onChanged]);

  const busyList = !!(list && list.sessions.some((s) => s.status === 'in_progress' || (s.status === 'completed' && s.report_status !== 'done' && s.report_status !== 'failed')));
  useEffect(() => {
    if (!busyList) return undefined;
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, [busyList, load]);

  async function buy() {
    setBuying(true);
    try { const r = await api('/api/portal/lexa/checkout', { method: 'POST', body: JSON.stringify({ currency }) }); window.location.assign(r.url); } catch (e) { flash(e.message, true); setBuying(false); }
  }
  async function discard(id) {
    try { await api('/api/portal/lexa/sessions/' + id + '/discard', { method: 'POST', body: '{}' }); load(); } catch (e) { flash(e.message, true); }
  }
  const cancelSetup = () => { clearLexaIntent(); setKind(null); };

  const cvGate = (
    <div className="gtr-card gtr2-pad">
      <div className="gtpt-cvgate gtp2-cvgate">
        <div className="gtpt-cvgate-h">Your CV first</div>
        <p className="gtpt-cvgate-p">Lexa builds every session around your own experience, so she needs your CV. Add it to your profile, then come back here. {intent && intent.role ? 'The role you picked will be waiting.' : ''}</p>
        {onNeedCv && <button type="button" className="gtr2-btn gtr2-btn--primary" onClick={onNeedCv}>Add your CV</button>}
      </div>
    </div>
  );

  if (error === 'no_profile') return <div className="gtr2-body"><div className="gtr2-main">{cvGate}</div></div>;
  if (error) return <div className="gtr-card gtp2-state">{error}</div>;
  if (!state || !list) return <div className="gtr-card gtp2-state">Loading Lexa</div>;

  const open = list.sessions.find((s) => s.status === 'ready' || s.status === 'in_progress');
  const past = list.sessions.filter((s) => s !== open);
  const credits = state.credits;
  const price = (c) => (state.prices.find((p) => p.currency === c) || {}).label;

  return (
    <div className="gtr2-body">
      <div className="gtr2-main">
        <section className="gtr-card gtp2-lexahead">
          <span className="gtlx-orb gtlx-orb--lg" aria-hidden="true" />
          <div>
            <b>Lexa, your career and interview coach</b>
            <p>Practise the interview for a real role on video, or work through your next move with a coach. Lexa remembers you between sessions, so each one picks up where the last left off.</p>
          </div>
        </section>

        {open && (
          <section className="gtr-card gtp2-opensess">
            <span className="gtlx-orb" aria-hidden="true" />
            <div className="gtp2-opensess-t">
              <b>{open.status === 'in_progress' ? 'Your session is in progress' : 'Your session is ready'}</b>
              <span>{open.title}{open.status === 'in_progress' ? '. Carry on where you left off.' : '. The session is used when the call starts.'}</span>
            </div>
            {open.status === 'ready' && <button type="button" className="gtp2-linkbtn" onClick={() => discard(open.id)}>Discard</button>}
            <a className="gtr2-btn gtr2-btn--primary" href={'/lexa/' + open.token}>{open.status === 'in_progress' ? 'Carry on' : 'Start the call'}</a>
          </section>
        )}

        {!state.has_cv ? cvGate : !state.terms.agreed ? (
          <Terms terms={state.terms} api={api} onAgreed={load} />
        ) : !open && (kind ? (
          <Setup kind={kind} api={api} initialRole={intent && intent.kind === kind ? intent.role : ''} onCancel={cancelSetup}
            onNoCredit={() => { setNudgeBuy(true); setTimeout(() => setNudgeBuy(false), 2400); }} />
        ) : (
          <div className="gtp2-kinds">
            <button type="button" className="gtr-card gtp2-kind" onClick={() => setKind('interview')}>
              <span className="gtp2-kind-ic"><Ico d={ICONS.brief} size={18} /></span>
              <b>Practise an interview</b>
              <span className="gtp2-kind-p">A mock interview for a real role: one of ours, or any job you paste in.</span>
              <ul>
                <li><Ico d={ICONS.check} />Questions built from the role and your CV</li>
                <li><Ico d={ICONS.check} />Coaching after each answer, or a straight run</li>
                <li><Ico d={ICONS.check} />A report with a stronger version of each answer</li>
              </ul>
              <em>Choose a role<Ico d={ICONS.arrow} /></em>
            </button>
            <button type="button" className="gtr-card gtp2-kind" onClick={() => setKind('career')}>
              <span className="gtp2-kind-ic"><Ico d={ICONS.compass} size={18} /></span>
              <b>Career coaching</b>
              <span className="gtp2-kind-p">A face-to-face conversation with a coach about where you are and where you want to go.</span>
              <ul>
                <li><Ico d={ICONS.check} />Your CV, positioning and LinkedIn</li>
                <li><Ico d={ICONS.check} />Your next move, pay and offers</li>
                <li><Ico d={ICONS.check} />You leave with an agreed plan in writing</li>
              </ul>
              <em>Choose a topic<Ico d={ICONS.arrow} /></em>
            </button>
          </div>
        ))}

        <div className="gtr-card gtr2-tabcard">
          <div className="gtp2-cardhead">
            <div><b>Your sessions</b><span>Each with its recording, transcript and written report, yours to come back to.</span></div>
            <span className="gtr2-count">{past.length}</span>
          </div>
          <div className="gtr2-pad">
            {past.length === 0 ? (
              <div className="gtr2-soon">
                <span className="gtr2-soon-ico"><Ico d={ICONS.spark} /></span>
                <b>No sessions yet</b>
                <p>When you finish a session, the recording and Lexa's report appear here.</p>
              </div>
            ) : (
              <div className="gtr2-list">
                {past.map((s) => {
                  const st = statusOf(s);
                  const canOpen = s.status === 'completed';
                  return (
                    <button key={s.id} type="button" className={'gtr2-listrow' + (canOpen ? '' : ' gtr2-listrow--static')} disabled={!canOpen} onClick={() => setViewId(s.id)}>
                      <span className={'gtr2-listico gtp2-lxico gtp2-lxico--' + s.kind}><Ico d={s.kind === 'interview' ? ICONS.brief : ICONS.compass} /></span>
                      <span className="gtr2-listmain">
                        <b>{s.title}</b>
                        <span>{ukDate(s.started_at || s.created_at)}{s.duration_seconds ? ' · ' + mins(s.duration_seconds) : ''}{s.kind === 'interview' ? ' · ' + (s.mode === 'real' ? 'Real mode' : 'Practice mode') : ''}</span>
                      </span>
                      <span className={'gtivl-pill gtivl-pill--' + st.tone}>{st.label}</span>
                      {canOpen && <span className="gtp2-rowgo">{s.report_status === 'done' ? 'Watch and read' : 'Open'}</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      <aside className="gtr2-side">
        <div className={'gtr-card' + (nudgeBuy ? ' gtp2-nudge' : '')}>
          <div className="gtr-cardh">Your sessions</div>
          <div className="gtr-props">
            <div className="gtr-prop"><span className="gtr-prop-k">Ready to use</span><span className="gtr-prop-v gtp2-big">{credits <= 0 ? 'None' : credits}</span></div>
            <div className="gtr-prop"><span className="gtr-prop-k">Each session</span><span className="gtr-prop-v">About 20 minutes, with a report</span></div>
            <div className="gtr-prop"><span className="gtr-prop-k">Price</span><span className="gtr-prop-v">{price('gbp')} · {price('usd')} · {price('eur')}</span></div>
          </div>
          {state.payments_on ? (
            <div className="gtp2-buy">
              <div className="gtp2-pills" role="radiogroup" aria-label="Currency">
                {state.prices.map((p) => (
                  <button key={p.currency} type="button" role="radio" aria-checked={currency === p.currency}
                    className={'gtp2-pill' + (currency === p.currency ? ' gtp2-pill--on' : '')} onClick={() => setCurrency(p.currency)}>{p.label}</button>
                ))}
              </div>
              <button type="button" className="gtr2-btn" disabled={buying || !state.terms.agreed} onClick={buy}>{buying ? 'Opening payment' : 'Add a session'}</button>
              {!state.terms.agreed && <span className="gtp2-need">Agree to the terms first</span>}
              <span className="gtr2-muted">Paid through Stripe. Card, Apple Pay or Google Pay.</span>
            </div>
          ) : (
            <p className="gtr2-foot">Adding more sessions opens soon.</p>
          )}
        </div>

        <div className="gtr-card gtp2-mem">
          <div className="gtr-cardh">What Lexa remembers</div>
          {list.memory && list.memory.note
            ? <><p className="gtr2-prose">{list.memory.note}</p><p className="gtr2-foot">She reads this before each session and updates it after.</p></>
            : <p className="gtr2-muted gtp2-p">Nothing yet. After your first session Lexa keeps a short note of your goals and what to work on, and reads it before the next one.</p>}
        </div>

        <div className="gtr-card">
          <div className="gtr-cardh">Private to you</div>
          <ul className="gtp2-checks gtp2-checks--tight">
            <li><Ico d={ICONS.lock} /><span>Never shared with employers.</span></li>
            <li><Ico d={ICONS.check} /><span>Optional and separate. It never affects whether we put you forward, and applying is always free.</span></li>
            <li><Ico d={ICONS.cam} /><span>Sessions are recorded and saved on your profile. The Generative Talent Co team can see them.</span></li>
          </ul>
        </div>
      </aside>

      {viewId && <SessionModal id={viewId} api={api} getToken={getToken} onClose={() => { setViewId(null); load(); }} />}
      {toast && <div className={'gtc-toast' + (toast.bad ? ' gtc-toast--err' : '')} role="status">{toast.text}</div>}
    </div>
  );
}
